/**
 * ComplianceResultsTab.js
 * ─────────────────────────────────────────────────────────────────────────────
 * Reusable "Compliance Results" tab.
 * Usage: <ComplianceResultsTab selectedCloudProvider={selectedCloudProvider} />
 */
import React, { useState, useEffect, useCallback, useRef } from "react";
import { useParams } from "react-router-dom";
import { SafText, SafAlert, SafIcon, SafButton, SafDialog } from "@saffron/core-components/react";
import { callMiddleware } from "../../util/api";

const COMPLIANCE_DOC_ID = "compliance-testing";

// Rule statements/requirement levels are no longer embedded in the
// compliance-testing doc itself — they live in a separate "rules" container,
// one doc per standard (service_key) or pattern (pattern_ref_id), each
// containing a `rules[]` array of { rule_id, statement, requirement_level }.
const RULES_DB_NAME = process.env.REACT_APP_DB_SOURCE;
const RULES_CONTAINER_NAME = "agent_complience_rules";

// Per-provider naming for the non-compliant resource identifier / scope
// fields, since each cloud provider uses different terminology.
const PROVIDER_RESOURCE_FIELDS = {
  aws: {
    identifierField: "arn",
    identifierLabel: "ARN",
    scopeField: "account_id",
    scopeLabel: "Account ID",
  },
  azure: {
    identifierField: "resource_id",
    identifierLabel: "Resource ID",
    scopeField: "subscription_id",
    scopeLabel: "Subscription ID",
  },
  gcp: {
    identifierField: "resource_id",
    identifierLabel: "Resource ID",
    scopeField: "project_id",
    scopeLabel: "Project ID",
  },
  oci: {
    identifierField: "ocid",
    identifierLabel: "OCID",
    scopeField: "compartment_id",
    scopeLabel: "Compartment ID",
  },
};
const DEFAULT_RESOURCE_FIELDS = {
  identifierField: "resource_id",
  identifierLabel: "Resource ID",
  scopeField: "account_id",
  scopeLabel: "Account ID",
};
const getProviderResourceFields = (provider) =>
  PROVIDER_RESOURCE_FIELDS[(provider || "").toLowerCase()] ||
  DEFAULT_RESOURCE_FIELDS;

// Formats a 0-100 compliance score into a colored badge descriptor.
// Returns null when the score is not present (older docs / nothing evaluated).
const getScoreBadge = (score) => {
  if (score === null || score === undefined || Number.isNaN(score)) {
    return null;
  }
  const rounded = Math.round(score * 100) / 100;
  let color = "#1a7f37";
  let background = "#eaf7ec";
  if (rounded < 50) {
    color = "#d93025";
    background = "#fdecea";
  } else if (rounded < 80) {
    color = "#946200";
    background = "#fff4e0";
  }
  return { label: `${rounded}%`, color, background };
};

const ScoreBadge = ({ score, style }) => {
  const badge = getScoreBadge(score);
  if (!badge) return null;
  return (
    <span
      style={{
        padding: "2px 8px",
        borderRadius: "12px",
        fontSize: "12px",
        fontWeight: 600,
        color: badge.color,
        background: badge.background,
        whiteSpace: "nowrap",
        ...style,
      }}
    >
      {badge.label}
    </span>
  );
};

const WORKING_MESSAGES = [
  "Working on it…",
  "Checking compliance rules…",
  "Evaluating services…",
  "Almost there…",
];

const STATUS_STYLES = {
  compliant: { color: "#1a7f37", background: "#eaf7ec", label: "Compliant" },
  non_compliant: {
    color: "#d93025",
    background: "#fdecea",
    label: "Non-Compliant",
  },
  not_applicable: {
    color: "#555",
    background: "#eee",
    label: "Not Applicable",
  },
  insufficient_data: {
    color: "#946200",
    background: "#fff4e0",
    label: "Insufficient Data",
  },
  unknown: { color: "#666", background: "#f3f3f3", label: "Unknown" },
};
const getStatusStyle = (status) =>
  STATUS_STYLES[(status || "").toLowerCase()] || {
    color: "#666",
    background: "#f3f3f3",
    label: status || "Unknown",
  };

const STATUS_FILTER_OPTIONS = [
  { value: "compliant", label: "Compliant" },
  { value: "non_compliant", label: "Non-Compliant" },
];

const ComplianceResultsTab = ({ selectedCloudProvider, onOverallScoreChange }) => {
  const { assetId: urlAssetId } = useParams();
  const assetId = urlAssetId || "";

  const dbName = process.env.REACT_APP_DB_TARGET;
  const containerName = process.env.REACT_APP_CONTAINER_ASSET_PARTITION;
  const complianceEndpoint = process.env.REACT_APP_COMPLIANCE_TEST_AGENT;

  const [doc, setDoc] = useState(null);
  const [loadingDoc, setLoadingDoc] = useState(true);
  const [isChecking, setIsChecking] = useState(false);
  const [workingMessage, setWorkingMessage] = useState(WORKING_MESSAGES[0]);
  const [resultMessage, setResultMessage] = useState(""); // non-2xx response message
  const [error, setError] = useState("");

  const messageIntervalRef = useRef(null);

  const fetchComplianceDoc = useCallback(async () => {
    if (!assetId) return;
    setLoadingDoc(true);
    setError("");
    try {
      const data = await callMiddleware("get", {
        dbName,
        containerName,
        query: `SELECT * FROM c WHERE c.asset_id = '${assetId}' AND c.id = '${COMPLIANCE_DOC_ID}'`,
        partitionKey: assetId,
      });
      const record = Array.isArray(data) ? data[0] : data;
      setDoc(record && record.id ? record : null);
    } catch (e) {
      setError(e.message || "Failed to load compliance results.");
    } finally {
      setLoadingDoc(false);
    }
  }, [assetId, dbName, containerName]);

  useEffect(() => {
    fetchComplianceDoc();
  }, [fetchComplianceDoc]);

  // "Check Compliance" should only be runnable when at least one of the
  // following documents exists for the selected asset + provider:
  //   1. A doc with id "{provider}_snow_permit_workflow" (e.g.
  //      "aws_snow_permit_workflow" for AWS, "azure_snow_permit_workflow"
  //      for Azure), OR
  //   2. At least one doc whose id starts with "CP-{PROVIDER}" (e.g.
  //      "CP-AWS…" for AWS, "CP-AZURE…" for Azure), OR
  //   3. A doc with id "legacy_permit_answers_{provider}" (e.g.
  //      "legacy_permit_answers_aws" for AWS).
  // All patterns are scoped to the currently selected cloud provider.
  const [hasProdOpenRitm, setHasProdOpenRitm] = useState(false);
  const [ritmCheckLoading, setRitmCheckLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;

    const checkEnablingDocs = async () => {
      if (!assetId || !selectedCloudProvider) {
        setHasProdOpenRitm(false);
        return;
      }
      setRitmCheckLoading(true);
      try {
        const provider = selectedCloudProvider.toLowerCase();
        const snowWorkflowId = `${provider}_snow_permit_workflow`;
        const cpPrefix = `CP-${provider.toUpperCase()}`;
        const legacyPermitAnswersId = `legacy_permit_answers_${provider}`;
        const data = await callMiddleware("get", {
          dbName,
          containerName,
          query: `SELECT * FROM c WHERE c.asset_id = '${assetId}' AND (c.id = '${snowWorkflowId}' OR STARTSWITH(c.id, '${cpPrefix}') OR c.id = '${legacyPermitAnswersId}')`,
          partitionKey: assetId,
        });
        const found = Array.isArray(data) ? data.length > 0 : Boolean(data);
        if (!cancelled) setHasProdOpenRitm(found);
      } catch (e) {
        if (!cancelled) setHasProdOpenRitm(false);
      } finally {
        if (!cancelled) setRitmCheckLoading(false);
      }
    };

    checkEnablingDocs();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assetId, selectedCloudProvider, dbName, containerName]);

  useEffect(() => {
    return () => {
      if (messageIntervalRef.current) {
        clearInterval(messageIntervalRef.current);
      }
    };
  }, []);

  const handleCheckCompliance = async () => {
    if (
      !assetId ||
      !selectedCloudProvider ||
      isChecking ||
      !hasProdOpenRitm
    )
      return;
    setIsChecking(true);
    setResultMessage("");
    setError("");

    let idx = 0;
    setWorkingMessage(WORKING_MESSAGES[0]);
    messageIntervalRef.current = setInterval(() => {
      idx = (idx + 1) % WORKING_MESSAGES.length;
      setWorkingMessage(WORKING_MESSAGES[idx]);
    }, 2500);

    try {
      const response = await fetch(complianceEndpoint, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          asset_id: assetId,
          provider: (selectedCloudProvider || "").toLowerCase(),
          dry_run: false,
        }),
      });

      if (response.status === 200 || response.status === 201) {
        // The test agent persists results to Cosmos — pull the fresh doc.
        await fetchComplianceDoc();
      } else {
        let msg = "";
        try {
          const body = await response.json();
          msg = body.message || body.error || body.detail || JSON.stringify(body);
        } catch {
          msg = await response.text().catch(() => "");
        }
        setResultMessage(msg || `Request failed with status ${response.status}`);
      }
    } catch (e) {
      setResultMessage(e.message || "Failed to run compliance check.");
    } finally {
      if (messageIntervalRef.current) {
        clearInterval(messageIntervalRef.current);
        messageIntervalRef.current = null;
      }
      setIsChecking(false);
    }
  };

  const providerKey = (selectedCloudProvider || "").toLowerCase();
  const providerData =
    (doc &&
      providerKey &&
      Array.isArray(doc.results) &&
      doc.results.find((r) => (r.cloud_provider || "").toLowerCase() === providerKey)) ||
    null;

  // Notify the parent page (which renders this component inside a tab) of
  // the current overall compliance score, so it can be shown in the tab
  // label itself, consistent with how other tabs surface summary info.
  useEffect(() => {
    if (typeof onOverallScoreChange === "function") {
      onOverallScoreChange(
        providerData && typeof providerData.compliance_score === "number"
          ? providerData.compliance_score
          : null
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [providerData]);

  // Collect the ids (service_key for standards, pattern_ref_id for patterns)
  // that actually have rule evaluation data — rule statements are only
  // fetched for these, never for sources with an empty rule_evaluations[].
  // Kept separate by source type since each maps to a different Cosmos
  // partition key value ("standard" vs "pattern"), letting us issue
  // single-partition queries instead of an unpartitioned fan-out scan.
  const collectRuleSourceIds = (data) => {
    const standardIds = new Set();
    const patternIds = new Set();
    (data?.services || []).forEach((svc) => {
      (svc.standards || []).forEach((s) => {
        if (
          s.service_key &&
          Array.isArray(s.rule_evaluations) &&
          s.rule_evaluations.length > 0
        ) {
          standardIds.add(s.service_key);
        }
      });
      (svc.patterns || []).forEach((p) => {
        if (
          p.pattern_ref_id &&
          Array.isArray(p.rule_evaluations) &&
          p.rule_evaluations.length > 0
        ) {
          patternIds.add(p.pattern_ref_id);
        }
      });
    });
    return {
      standardIds: Array.from(standardIds),
      patternIds: Array.from(patternIds),
    };
  };

  const [rulesMap, setRulesMap] = useState({}); // sourceId -> { [rule_id]: { statement, requirement_level } }
  const [rulesLoading, setRulesLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;

    const quoteIds = (ids) =>
      ids.map((id) => `'${id.replace(/'/g, "''")}'`).join(", ");

    const fetchRuleDocs = async (ids, partitionKey) => {
      if (ids.length === 0) return [];
      const data = await callMiddleware("get", {
        dbName: RULES_DB_NAME,
        containerName: RULES_CONTAINER_NAME,
        query: `SELECT * FROM c WHERE c.id IN (${quoteIds(ids)})`,
        partitionKey,
      });
      return Array.isArray(data) ? data : data ? [data] : [];
    };

    const loadRuleStatements = async () => {
      const { standardIds, patternIds } = collectRuleSourceIds(providerData);
      if (standardIds.length === 0 && patternIds.length === 0) {
        setRulesMap({});
        return;
      }
      setRulesLoading(true);
      try {
        // Each group targets a single known partition ("standard" /
        // "pattern"), so these are cheap single-partition queries run in
        // parallel instead of one unpartitioned cross-partition scan.
        const [standardRecords, patternRecords] = await Promise.all([
          fetchRuleDocs(standardIds, "standard"),
          fetchRuleDocs(patternIds, "pattern"),
        ]);
        const records = [...standardRecords, ...patternRecords];
        const map = {};
        records.forEach((rec) => {
          const ruleMap = {};
          (rec.rules || []).forEach((r) => {
            if (r.rule_id) {
              ruleMap[r.rule_id] = {
                statement: r.statement,
                requirement_level: r.requirement_level,
              };
            }
          });
          map[rec.id] = ruleMap;
        });
        if (!cancelled) setRulesMap(map);
      } catch (e) {
        if (!cancelled) setRulesMap({});
      } finally {
        if (!cancelled) setRulesLoading(false);
      }
    };

    loadRuleStatements();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [providerData]);

  // Filters — shared across the provider's service list so they reset
  // naturally whenever the user switches provider (new providerData ref).
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState([]); // [] = all results; otherwise selected statuses

  useEffect(() => {
    setSearchTerm("");
    setStatusFilter([]);
  }, [providerKey]);

  return (
    <div style={{ padding: "16px 24px" }}>
      <SafAlert
        appearance="warning"
        alertType="inline"
        style={{ marginBottom: "16px" }}
      >
        <strong>This feature is currently in beta. Results and functionality may change, and some information may be incomplete or inaccurate as we continue to improve and refine the experience.</strong>
      </SafAlert>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "16px",
          flexWrap: "wrap",
          gap: "12px",
        }}
      >
        <div>
          <SafText appearance="heading-md" style={{ display: "block" }}>
            Compliance Results
          </SafText>
          <SafText
            appearance="body-default-sm"
            style={{ color: "#666", display: "block", marginTop: "4px" }}
          >
            Run automated compliance checks against configured patterns for
            the selected cloud provider.
          </SafText>
        </div>
        <SafButton
          appearance="primary"
          onClick={handleCheckCompliance}
          disabled={
            isChecking ||
            !selectedCloudProvider ||
            !assetId ||
            ritmCheckLoading ||
            !hasProdOpenRitm
          }
        >
          {isChecking ? workingMessage : "Check Compliance"}
        </SafButton>
      </div>

      {!selectedCloudProvider && (
        <SafAlert appearance="informational" alertType="inline">
          Select a cloud provider to view or run compliance results.
        </SafAlert>
      )}

      {selectedCloudProvider &&
        !ritmCheckLoading &&
        !hasProdOpenRitm && (
          <SafAlert
            appearance="informational"
            alertType="inline"
            style={{ marginBottom: "12px" }}
          >
            The "Check Compliance" option is enabled only if you have
            submitted a Cloud Permit request at least once for the
            selected cloud provider.
          </SafAlert>
        )}

      {error && (
        <SafAlert
          appearance="danger"
          alertType="inline"
          style={{ marginBottom: "12px" }}
        >
          {error}
        </SafAlert>
      )}

      {resultMessage && (
        <SafAlert
          appearance="warning"
          alertType="inline"
          style={{ marginBottom: "12px" }}
        >
          {resultMessage}
        </SafAlert>
      )}

      {isChecking && (
        <div style={{ padding: "24px", textAlign: "center", color: "#666" }}>
          <SafIcon
            icon-name="spinner"
            appearance="solid"
            style={{ marginRight: 8 }}
          />
          {workingMessage}
        </div>
      )}

      {!isChecking && loadingDoc && (
        <div style={{ padding: "24px", textAlign: "center", color: "#666" }}>
          Loading compliance results...
        </div>
      )}

      {!isChecking &&
        !loadingDoc &&
        selectedCloudProvider &&
        (providerData ? (
          <>
            <ComplianceFilters
              searchTerm={searchTerm}
              onSearchChange={setSearchTerm}
              statusFilter={statusFilter}
              onStatusChange={setStatusFilter}
            />
            <ComplianceProviderResults
              providerData={providerData}
              searchTerm={searchTerm}
              statusFilter={statusFilter}
              rulesMap={rulesMap}
              rulesLoading={rulesLoading}
            />
          </>
        ) : (
          <div
            style={{ padding: "24px", textAlign: "center", color: "#666" }}
          >
            No compliance results available yet for{" "}
            {selectedCloudProvider.toUpperCase()}. Click "Check Compliance"
            to run a check.
          </div>
        ))}
    </div>
  );
};

const FILTER_INPUT_STYLE = {
  padding: "6px 10px",
  border: "1px solid #d0d5dd",
  borderRadius: "6px",
  fontSize: "13px",
};

const ComplianceFilters = ({
  searchTerm,
  onSearchChange,
  statusFilter,
  onStatusChange,
}) => {
  const [statusMenuOpen, setStatusMenuOpen] = useState(false);
  const hasActiveFilters = searchTerm.trim() !== "" || statusFilter.length > 0;

  const toggleStatus = (value) => {
    if (statusFilter.includes(value)) {
      onStatusChange(statusFilter.filter((v) => v !== value));
    } else {
      onStatusChange([...statusFilter, value]);
    }
  };

  const statusButtonLabel =
    statusFilter.length === 0
      ? "All results"
      : statusFilter.length === 1
      ? STATUS_FILTER_OPTIONS.find((o) => o.value === statusFilter[0])?.label ||
        statusFilter[0]
      : `${statusFilter.length} results selected`;

  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        flexWrap: "wrap",
        gap: "10px",
        marginBottom: "16px",
        padding: "12px",
        background: "#fafafa",
        border: "1px solid #eee",
        borderRadius: "8px",
      }}
    >
      <input
        type="text"
        placeholder="Search by service / technology name…"
        value={searchTerm}
        onChange={(e) => onSearchChange(e.target.value)}
        style={{ ...FILTER_INPUT_STYLE, minWidth: "220px", flex: "1 1 220px" }}
      />

      <div style={{ position: "relative" }}>
        <button
          type="button"
          onClick={() => setStatusMenuOpen((open) => !open)}
          style={{
            ...FILTER_INPUT_STYLE,
            background: "#fff",
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            gap: "6px",
            minWidth: "160px",
            justifyContent: "space-between",
          }}
          aria-haspopup="listbox"
          aria-expanded={statusMenuOpen}
        >
          {statusButtonLabel}
          <span style={{ fontSize: "10px" }}>▾</span>
        </button>

        {statusMenuOpen && (
          <>
            <div
              onClick={() => setStatusMenuOpen(false)}
              style={{
                position: "fixed",
                inset: 0,
                zIndex: 10,
              }}
            />
            <div
              style={{
                position: "absolute",
                top: "calc(100% + 4px)",
                left: 0,
                zIndex: 11,
                background: "#fff",
                border: "1px solid #d0d5dd",
                borderRadius: "8px",
                boxShadow: "0 4px 12px rgba(0,0,0,0.12)",
                padding: "8px",
                minWidth: "200px",
              }}
            >
              {STATUS_FILTER_OPTIONS.map((opt) => (
                <label
                  key={opt.value}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    padding: "6px 8px",
                    fontSize: "13px",
                    cursor: "pointer",
                    borderRadius: "4px",
                  }}
                >
                  <input
                    type="checkbox"
                    checked={statusFilter.includes(opt.value)}
                    onChange={() => toggleStatus(opt.value)}
                  />
                  {opt.label}
                </label>
              ))}
              {statusFilter.length > 0 && (
                <button
                  type="button"
                  onClick={() => onStatusChange([])}
                  style={{
                    marginTop: "6px",
                    width: "100%",
                    background: "none",
                    border: "none",
                    color: "#d64000",
                    fontWeight: 600,
                    fontSize: "12px",
                    cursor: "pointer",
                    padding: "4px",
                  }}
                >
                  Clear selection
                </button>
              )}
            </div>
          </>
        )}
      </div>

      {hasActiveFilters && (
        <button
          type="button"
          onClick={() => {
            onSearchChange("");
            onStatusChange([]);
          }}
          style={{
            ...FILTER_INPUT_STYLE,
            background: "#fff",
            cursor: "pointer",
            color: "#d64000",
            fontWeight: 600,
          }}
        >
          Clear filters
        </button>
      )}
    </div>
  );
};

const ComplianceProviderResults = ({
  providerData,
  searchTerm,
  statusFilter,
  rulesMap,
  rulesLoading,
}) => {
  const [resourceDialog, setResourceDialog] = useState(null); // { statement, groups, totalCount, fields } | null

  const resourceFields = getProviderResourceFields(providerData.cloud_provider);

  // Build a flat list of evaluated rules for a service by combining its
  // "standards" and "patterns" sources (only those that actually have rule
  // evaluation data), resolving each rule's statement/requirement_level via
  // rulesMap, and resolving each failure group's resource_refs against the
  // service's own resources[] list.
  const buildRuleRows = (svc) => {
    const resourceByRef = {};
    (svc.resources || []).forEach((res) => {
      if (res.resource_ref) resourceByRef[res.resource_ref] = res;
    });

    const sources = [
      ...(svc.standards || []).map((s) => ({
        sourceType: "standard",
        sourceId: s.service_key,
        ruleEvaluations: s.rule_evaluations,
      })),
      ...(svc.patterns || []).map((p) => ({
        sourceType: "pattern",
        sourceId: p.pattern_ref_id,
        ruleEvaluations: p.rule_evaluations,
        complianceScore: p.compliance_score,
        compliantCount: p.compliant_count,
        nonCompliantCount: p.non_compliant_count,
        evaluatedCount: p.evaluated_count,
      })),
    ];

    const rows = [];
    sources.forEach((src) => {
      if (!Array.isArray(src.ruleEvaluations) || src.ruleEvaluations.length === 0) {
        return;
      }
      const ruleDefs = rulesMap[src.sourceId] || {};
      src.ruleEvaluations.forEach((rule, idx) => {
        const ruleDef = ruleDefs[rule.rule_ref] || {};
        const failureGroups = Array.isArray(rule.failure_groups)
          ? rule.failure_groups
          : [];
        const resolvedGroups = failureGroups.map((g) => ({
          groupRef: g.group_ref,
          evidence: g.evidence,
          actualValue: g.actual_value,
          resources: (g.resource_refs || [])
            .map((ref) => resourceByRef[ref])
            .filter(Boolean),
        }));
        const nonCompliantCount =
          typeof rule.non_compliant_resource_count === "number"
            ? rule.non_compliant_resource_count
            : resolvedGroups.reduce((sum, g) => sum + g.resources.length, 0);
        rows.push({
          key: `${src.sourceId}-${rule.rule_ref || idx}`,
          sourceType: src.sourceType,
          sourceId: src.sourceId,
          patternScore: src.complianceScore,
          patternCompliantCount: src.compliantCount,
          patternNonCompliantCount: src.nonCompliantCount,
          patternEvaluatedCount: src.evaluatedCount,
          statement: ruleDef.statement || rule.rule_ref || "—",
          status: rule.status,
          evidence: rule.evidence,
          failureGroups: resolvedGroups,
          nonCompliantCount,
        });
      });
    });
    return rows;
  };

  const allServices = Array.isArray(providerData.services)
    ? providerData.services
    : [];
  const staleLinks = Array.isArray(providerData.stale_pattern_links)
    ? providerData.stale_pattern_links
    : [];
  const noPatternAttachedServices = Array.isArray(
    providerData.no_pattern_attached_services
  )
    ? providerData.no_pattern_attached_services
    : [];
  const patternNoRulesServices = Array.isArray(
    providerData.pattern_no_rules_services
  )
    ? providerData.pattern_no_rules_services
    : [];
  const noRulesServices = [
    ...noPatternAttachedServices,
    ...patternNoRulesServices,
  ];

  const term = (searchTerm || "").trim().toLowerCase();

  // Only compliant / non_compliant results are relevant here — other
  // statuses (not_applicable, insufficient_data, unknown) are always
  // excluded regardless of the active status filter selection.
  const VISIBLE_STATUSES = ["compliant", "non_compliant"];

  // Apply the status filter first, then drop services left with zero
  // matching rules; apply the name search at the service level.
  const services = allServices
    .filter(
      (svc) => !term || (svc.technology_name || "").toLowerCase().includes(term)
    )
    .map((svc) => {
      const allRows = buildRuleRows(svc);
      const filteredRows = allRows.filter((row) => {
        const status = (row.status || "").toLowerCase();
        if (!VISIBLE_STATUSES.includes(status)) return false;
        return statusFilter.length === 0 || statusFilter.includes(status);
      });
      const standardRows = filteredRows.filter(
        (row) => row.sourceType !== "pattern"
      );
      const patternGroupsMap = {};
      filteredRows
        .filter((row) => row.sourceType === "pattern")
        .forEach((row) => {
          if (!patternGroupsMap[row.sourceId]) {
            patternGroupsMap[row.sourceId] = {
              patternRefId: row.sourceId,
              score: row.patternScore,
              compliantCount: row.patternCompliantCount,
              nonCompliantCount: row.patternNonCompliantCount,
              evaluatedCount: row.patternEvaluatedCount,
              rows: [],
            };
          }
          patternGroupsMap[row.sourceId].rows.push(row);
        });
      const patternGroups = Object.values(patternGroupsMap);
      return { ...svc, ruleRows: filteredRows, standardRows, patternGroups };
    })
    .filter((svc) => svc.ruleRows.length > 0);

  // Services with no rules configured have no status at all, so they only
  // make sense to show when no status filter is active; the name search
  // still applies to them.
  const filterByTerm = (name) => !term || (name || "").toLowerCase().includes(term);
  const filteredNoPatternAttachedServices =
    statusFilter.length === 0
      ? noPatternAttachedServices.filter(filterByTerm)
      : [];
  const filteredPatternNoRulesServices =
    statusFilter.length === 0
      ? patternNoRulesServices.filter(filterByTerm)
      : [];
  const filteredNoRulesServicesCount =
    filteredNoPatternAttachedServices.length +
    filteredPatternNoRulesServices.length;

  return (
    <div>
      <SafText
        appearance="body-default-sm"
        style={{ color: "#666", display: "block", marginBottom: "12px" }}
      >
        Last evaluated:{" "}
        {providerData.evaluated_at
          ? new Date(providerData.evaluated_at).toLocaleString()
          : "N/A"}
        {" · "}
        {services.length} of {allServices.length} service(s) shown
        {noRulesServices.length > 0 &&
          ` · ${filteredNoRulesServicesCount} of ${noRulesServices.length} without rules shown`}
        {rulesLoading && " · Loading rule descriptions…"}
        
      </SafText>

      {staleLinks.length > 0 && (
        <SafAlert
          appearance="warning"
          alertType="inline"
          style={{ marginBottom: "16px" }}
        >
          <strong>{staleLinks.length}</strong> service(s) reference pattern(s)
          that are no longer connected to their technology — these should be
          reviewed and updated:
          <ul style={{ margin: "8px 0 0 20px" }}>
            {staleLinks.map((s) => (
              <li key={s.service_id}>
                {s.technology_name}:{" "}
                {Array.isArray(s.stale_pattern_ref_ids)
                  ? s.stale_pattern_ref_ids.join(", ")
                  : ""}
              </li>
            ))}
          </ul>
        </SafAlert>
      )}

      {allServices.length === 0 ? (
        <div style={{ padding: "16px", textAlign: "center", color: "#666" }}>
          No services were tested for this provider.
        </div>
      ) : services.length === 0 ? (
        <div style={{ padding: "16px", textAlign: "center", color: "#666" }}>
          No results match the current filters.
        </div>
      ) : (
        services.map((svc) => (
          <div
            key={svc.service_id}
            style={{
              marginBottom: "20px",
              border: "1px solid #e5e7eb",
              borderRadius: "8px",
              overflow: "hidden",
            }}
          >
            <div
              style={{
                padding: "10px 16px",
                background: "#f9fafb",
                fontWeight: 600,
                display: "flex",
                alignItems: "center",
                gap: "8px",
              }}
            >
              <span>{svc.technology_name}</span>
            </div>
            <div style={{ padding: "0 16px", overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                  <tr>
                    <th
                      style={{
                        textAlign: "left",
                        padding: "8px 4px",
                        width: "45%",
                      }}
                    >
                      Statement
                    </th>
                    <th
                      style={{
                        textAlign: "left",
                        padding: "8px 4px",
                        width: "15%",
                      }}
                    >
                      Result
                    </th>
                    <th
                      style={{
                        textAlign: "left",
                        padding: "8px 4px",
                        width: "40%",
                      }}
                    >
                      Evidence
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {(svc.standardRows || []).map((row) => {
                    const statusStyle = getStatusStyle(row.status);
                    return (
                      <tr key={row.key} style={{ borderTop: "1px solid #eee" }}>
                        <td style={{ padding: "8px 4px", verticalAlign: "top" }}>
                          {row.statement}
                        </td>
                        <td style={{ padding: "8px 4px", verticalAlign: "top" }}>
                          <span
                            style={{
                              padding: "2px 8px",
                              borderRadius: "12px",
                              fontSize: "12px",
                              fontWeight: 600,
                              color: statusStyle.color,
                              background: statusStyle.background,
                              whiteSpace: "nowrap",
                            }}
                          >
                            {statusStyle.label}
                          </span>
                        </td>
                        <td
                          style={{
                            padding: "8px 4px",
                            verticalAlign: "top",
                            color: "#444",
                            fontSize: "13px",
                          }}
                        >
                          {row.nonCompliantCount > 0 ? (
                            <button
                              type="button"
                              onClick={() =>
                                setResourceDialog({
                                  statement: row.statement,
                                  groups: row.failureGroups,
                                  totalCount: row.nonCompliantCount,
                                  fields: resourceFields,
                                })
                              }
                              style={{
                                background: "none",
                                border: "none",
                                padding: 0,
                                margin: 0,
                                color: "#0b5cad",
                                textDecoration: "underline",
                                cursor: "pointer",
                                fontSize: "13px",
                                fontWeight: 600,
                              }}
                            >
                              {row.nonCompliantCount} non-compliant resource
                              {row.nonCompliantCount === 1 ? "" : "s"}
                            </button>
                          ) : (
                            row.evidence || "—"
                          )}
                        </td>
                      </tr>
                    );
                  })}

                </tbody>
              </table>
            </div>

            {(svc.patternGroups || []).length > 0 && (
              <div
                style={{
                  padding: "12px 16px",
                  borderTop: "1px solid #e5e7eb",
                  background: "#fcfcfd",
                }}
              >
                {svc.patternGroups.map((group) => (
                  <div
                    key={group.patternRefId}
                    style={{
                      marginBottom: "16px",
                      border: "1px solid #e5e7eb",
                      borderRadius: "6px",
                      overflow: "hidden",
                      background: "#fff",
                    }}
                  >
                    <div
                      style={{
                        padding: "8px 12px",
                        background: "#f3f4f6",
                        fontWeight: 600,
                        fontSize: "13px",
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                      }}
                    >
                      <SafIcon icon-name="sitemap" appearance="solid" />
                      <span>Pattern: {group.patternRefId}</span>
                    </div>
                    <div style={{ padding: "0 12px", overflowX: "auto" }}>
                      <table style={{ width: "100%", borderCollapse: "collapse" }}>
                        <thead>
                          <tr>
                            <th
                              style={{
                                textAlign: "left",
                                padding: "8px 4px",
                                width: "45%",
                              }}
                            >
                              Statement
                            </th>
                            <th
                              style={{
                                textAlign: "left",
                                padding: "8px 4px",
                                width: "15%",
                              }}
                            >
                              Result
                            </th>
                            <th
                              style={{
                                textAlign: "left",
                                padding: "8px 4px",
                                width: "40%",
                              }}
                            >
                              Evidence
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {group.rows.map((row) => {
                            const statusStyle = getStatusStyle(row.status);
                            return (
                              <tr
                                key={row.key}
                                style={{ borderTop: "1px solid #eee" }}
                              >
                                <td
                                  style={{ padding: "8px 4px", verticalAlign: "top" }}
                                >
                                  {row.statement}
                                </td>
                                <td
                                  style={{ padding: "8px 4px", verticalAlign: "top" }}
                                >
                                  <span
                                    style={{
                                      padding: "2px 8px",
                                      borderRadius: "12px",
                                      fontSize: "12px",
                                      fontWeight: 600,
                                      color: statusStyle.color,
                                      background: statusStyle.background,
                                      whiteSpace: "nowrap",
                                    }}
                                  >
                                    {statusStyle.label}
                                  </span>
                                </td>
                                <td
                                  style={{
                                    padding: "8px 4px",
                                    verticalAlign: "top",
                                    color: "#444",
                                    fontSize: "13px",
                                  }}
                                >
                                  {row.nonCompliantCount > 0 ? (
                                    <button
                                      type="button"
                                      onClick={() =>
                                        setResourceDialog({
                                          statement: row.statement,
                                          groups: row.failureGroups,
                                          totalCount: row.nonCompliantCount,
                                          fields: resourceFields,
                                        })
                                      }
                                      style={{
                                        background: "none",
                                        border: "none",
                                        padding: 0,
                                        margin: 0,
                                        color: "#0b5cad",
                                        textDecoration: "underline",
                                        cursor: "pointer",
                                        fontSize: "13px",
                                        fontWeight: 600,
                                      }}
                                    >
                                      {row.nonCompliantCount} non-compliant
                                      resource{row.nonCompliantCount === 1 ? "" : "s"}
                                    </button>
                                  ) : (
                                    row.evidence || "—"
                                  )}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        ))
      )}

      {filteredNoPatternAttachedServices.length > 0 && (
        <div
          style={{
            marginTop: "20px",
            marginBottom: "16px",
            border: "1px solid #e5e7eb",
            borderRadius: "8px",
            overflow: "hidden",
          }}
        >
          <div
            style={{
              padding: "10px 16px",
              background: "#f9fafb",
              fontWeight: 600,
              display: "flex",
              alignItems: "center",
              gap: "8px",
            }}
          >
            <SafIcon icon-name="info-circle" appearance="solid" />
            Services With No Pattern Attached ({filteredNoPatternAttachedServices.length})
          </div>
          <div style={{ padding: "12px 16px" }}>
            <SafText
              appearance="body-default-sm"
              style={{ color: "#666", display: "block", marginBottom: "8px" }}
            >
              These services were detected for this asset but do not have any
              architecture pattern attached yet, so they were skipped during
              the last check.
            </SafText>
            <ul style={{ margin: 0, paddingLeft: "20px" }}>
              {filteredNoPatternAttachedServices.map((name) => (
                <li key={name} style={{ fontSize: "13px", color: "#444" }}>
                  {name}
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}

      <SafDialog
        modal={true}
        is-alert={false}
        is-header={true}
        is-footer={true}
        no-focus-trap={false}
        dialog-title="Non-Compliant Resources"
        hidden={!resourceDialog}
        onClose={() => setResourceDialog(null)}
        className="compliance-resource-dialog"
      >
        {resourceDialog && (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              width: "100%",
              height: "100%",
              minHeight: 0,
            }}
          >
            <SafText
              appearance="body-default-sm"
              style={{ color: "#444", display: "block", marginBottom: "12px" }}
            >
              {resourceDialog.statement}
            </SafText>
            <SafText
              appearance="body-default-sm"
              style={{ color: "#666", display: "block", marginBottom: "12px" }}
            >
              {resourceDialog.totalCount} non-compliant resource
              {resourceDialog.totalCount === 1 ? "" : "s"}
            </SafText>
            <div style={{ flex: 1, minHeight: 0, overflow: "auto" }}>
              {!resourceDialog.groups || resourceDialog.groups.length === 0 ? (
                <div style={{ padding: "16px", color: "#666" }}>
                  No detailed resource information is available for this
                  rule.
                </div>
              ) : (
                resourceDialog.groups.map((group, gi) => (
                  <div
                    key={group.groupRef || gi}
                    style={{
                      marginBottom: "20px",
                      border: "1px solid #e5e7eb",
                      borderRadius: "8px",
                      overflow: "hidden",
                    }}
                  >
                    {group.evidence && (
                      <div
                        style={{
                          padding: "8px 12px",
                          background: "#f9fafb",
                          fontSize: "13px",
                          color: "#444",
                          borderBottom: "1px solid #e5e7eb",
                        }}
                      >
                        {group.evidence}
                      </div>
                    )}
                    {group.resources.length === 0 ? (
                      <div style={{ padding: "12px", color: "#666", fontSize: "13px" }}>
                        No detailed resource information is available for
                        this group.
                      </div>
                    ) : (
                      <table style={{ width: "100%", borderCollapse: "collapse" }}>
                        <thead>
                          <tr>
                            <th
                              style={{
                                textAlign: "left",
                                padding: "8px 4px",
                                position: "sticky",
                                top: 0,
                                background: "#fff",
                                borderBottom: "1px solid #e5e7eb",
                              }}
                            >
                              {resourceDialog.fields.identifierLabel}
                            </th>
                            <th
                              style={{
                                textAlign: "left",
                                padding: "8px 4px",
                                position: "sticky",
                                top: 0,
                                background: "#fff",
                                borderBottom: "1px solid #e5e7eb",
                              }}
                            >
                              {resourceDialog.fields.scopeLabel}
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {group.resources.map((res, i) => (
                            <tr
                              key={
                                res[resourceDialog.fields.identifierField] || i
                              }
                              style={{ borderTop: "1px solid #eee" }}
                            >
                              <td
                                style={{
                                  padding: "8px 4px",
                                  verticalAlign: "top",
                                  wordBreak: "break-all",
                                  fontSize: "12px",
                                  color: "#555",
                                }}
                              >
                                {res[resourceDialog.fields.identifierField]}
                              </td>
                              <td style={{ padding: "8px 4px", verticalAlign: "top" }}>
                                {res[resourceDialog.fields.scopeField]}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}
                  </div>
                ))
              )}
            </div>
            <div
              style={{
                display: "flex",
                justifyContent: "flex-end",
                marginTop: "12px",
              }}
            >
              <SafButton
                appearance="secondary"
                onClick={() => setResourceDialog(null)}
              >
                Close
              </SafButton>
            </div>
          </div>
        )}

      </SafDialog>
    </div>
  );
};

export default ComplianceResultsTab;

